import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Club, Member, RsvpStatus } from "@pagemates/shared";
import { askForPush } from "./PushAsk";
import { api, describeError } from "../lib/api";
import { Avatar } from "./ui";
import { ProfileFace } from "./Profile";
import { CalendarIcon, CheckIcon, CloseIcon, PencilIcon } from "./Icons";
import { meeting } from "@pagemates/shared/copy";

const txt = meeting.meetingCard;

const DAY = 86_400_000;
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (days < -1) return txt.daysAgo(-days);
  if (days === -1) return txt.yesterday;
  if (days === 0) return txt.today;
  if (days === 1) return txt.tomorrow;
  return txt.inDays(days);
}

/** The next meeting: when, where, who hosts, your RSVP, who's going. Admins edit it in place. */
export function MeetingCard({ club, members, isAdmin, agendaLink = false, hero = false }: { club: Club; members: Member[]; isAdmin: boolean; agendaLink?: boolean; hero?: boolean }) {
  const qc = useQueryClient();
  const m = club.meeting;
  const [editing, setEditing] = useState(false);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["meeting-detail"] })]);
  // The first answer is the moment to offer a reminder the day before (askForPush only asks once, ever).
  const rsvp = useMutation({ mutationFn: (status: RsvpStatus) => api.setRsvp(club.id, status), onSuccess: (_d, status) => { if (status !== "cant") askForPush("rsvp"); return refresh(); } });
  if (!m && !isAdmin) return hero
    ? <div className="mhero"><div className="date">{txt.noDateYet}</div><div className="sub">{txt.anAdminSetsThe}</div></div>
    : <section className="card quiet-card"><CalendarIcon /><div><b>{txt.noMeetingOnThe}</b><div className="d">{txt.anAdminSetsThe2}</div></div></section>;
  if (!m || editing) return <MeetingEditor club={club} members={members} onDone={() => setEditing(false)} />;
  const past = new Date(m.at).getTime() < Date.now();
  const stale = m.rsvp.mine?.stale;
  const btn = (s: RsvpStatus, label: string) => (
    <button type="button" className={`btn sm${m.rsvp.mine?.status === s && !stale ? " on" : ""}`} disabled={rsvp.isPending} onClick={() => rsvp.mutate(s)} aria-pressed={m.rsvp.mine?.status === s && !stale}>
      {m.rsvp.mine?.status === s && !stale && <CheckIcon width={14} height={14} />}{label}
    </button>
  );
  if (hero) {
    return (
      <section className="meet mhero">
        {isAdmin && <button type="button" className="pencil" aria-label={txt.editTheMeeting} onClick={() => setEditing(true)}><PencilIcon width={15} height={15} /><span aria-hidden="true">{txt.edit}</span></button>}
        <span className="tile" aria-hidden="true"><i>{new Date(m.at).toLocaleDateString(undefined, { month: "short" })}</i><b>{new Date(m.at).getDate()}</b></span>
        <div style={{ minWidth: 0 }}>
          <div className="date">{new Date(m.at).toLocaleDateString(undefined, { weekday: "long" })} · {new Date(m.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</div>
          <div className="sub">{countdown(m.at)}{m.location ? ` · ${m.location}` : ""}{m.host ? txt.isHosting(m.host.name) : ""}</div>
        </div>
        {m.previousAt && !past && stale && m.rsvp.mine && (
          <div className="flagline">
            <span>{txt.theDateMovedFrom(when(m.previousAt))}<b>{{ going: txt.going, cant: txt.canT, maybe: txt.maybe }[m.rsvp.mine.status]}</b>. Still true?</span>
            <button type="button" className="btn sm" disabled={rsvp.isPending} onClick={() => rsvp.mutate(m.rsvp.mine!.status)}>{txt.yesStill({ going: txt.going, cant: txt.canT, maybe: txt.maybe }[m.rsvp.mine.status])}</button>
          </div>
        )}
        {!past ? (
          <div className="rsvp">
            {btn("going", txt.going2)}{btn("cant", txt.canT2)}{btn("maybe", txt.maybe2)}
            <span className="faces">{m.rsvp.goingMembers.slice(0, 6).map((u) => <ProfileFace key={u.id} user={u} />)}</span>
            <span className="small muted">{txt.going3(m.rsvp.going, m.rsvp.maybe ? txt.maybe3(m.rsvp.maybe) : "")}</span>
          </div>
        ) : <div className="small muted">{txt.thisMeetingHasPassed}</div>}
        {rsvp.isError && <p className="error small">{describeError(rsvp.error)}</p>}
      </section>
    );
  }
  return (
    <section className="card meet">
      {isAdmin && <button type="button" className="pencil" aria-label={txt.editTheMeeting} onClick={() => setEditing(true)}><PencilIcon width={15} height={15} /><span aria-hidden="true">{txt.edit}</span></button>}
      <div>
        <div className="when">{when(m.at)}</div>
        <div className="d">{countdown(m.at)}{m.location ? ` · ${m.location}` : ""}</div>
      </div>
      {m.previousAt && !past && stale && m.rsvp.mine && (
          <div className="flagline">
            <span>{txt.theDateMovedFrom(when(m.previousAt))}<b>{{ going: txt.going, cant: txt.canT, maybe: txt.maybe }[m.rsvp.mine.status]}</b>. Still true?</span>
            <button type="button" className="btn sm" disabled={rsvp.isPending} onClick={() => rsvp.mutate(m.rsvp.mine!.status)}>{txt.yesStill({ going: txt.going, cant: txt.canT, maybe: txt.maybe }[m.rsvp.mine.status])}</button>
          </div>
        )}
      {m.host && <div className="host"><Avatar name={m.host.name} image={m.host.image} /><span>{txt.isHosting2(m.host.name)}</span></div>}
      {!past && (
        <div className="rsvp">
          {btn("going", txt.going2)}{btn("cant", txt.canT2)}{btn("maybe", txt.maybe2)}
          <span className="faces">{m.rsvp.goingMembers.slice(0, 5).map((u) => <ProfileFace key={u.id} user={u} />)}</span>
        </div>
      )}
      <div className="small muted">
        {past ? txt.thisMeetingHasPassed : txt.going4(m.rsvp.going, m.rsvp.maybe ? txt.maybe3(m.rsvp.maybe) : "", m.rsvp.staleYes ? txt.saidYesBeforeThe(m.rsvp.staleYes) : "")}
        {agendaLink && <>{" · "}<Link to="/clubs/$clubId/meeting" params={{ clubId: club.slug }} search={m.clubBookId ? { book: m.clubBookId } : {}}>{txt.openTheAgenda}</Link></>}
      </div>
      {rsvp.isError && <p className="error small">{describeError(rsvp.error)}</p>}
    </section>
  );
}

function toLocalInput(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function MeetingEditor({ club, members, onDone }: { club: Club; members: Member[]; onDone: () => void }) {
  const qc = useQueryClient();
  const m = club.meeting;
  const [at, setAt] = useState(m ? toLocalInput(m.at) : "");
  const [location, setLocation] = useState(m?.location ?? "");
  const [hostId, setHostId] = useState(m?.host?.id ?? "");
  const [noDate, setNoDate] = useState(false); // Save is always a real button; a missing date is said on submit
  const save = useMutation({
    mutationFn: (input: { at: string | null; location?: string | null; hostId?: string | null }) => api.setMeeting(club.id, input),
    onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["meeting-detail"] }), qc.invalidateQueries({ queryKey: ["questions"] })]); onDone(); },
  });
  const moving = m && at && new Date(at).toISOString() !== m.at && new Date(m.at) > new Date();
  return (
    <form className="card stack meet-edit" noValidate onSubmit={(e) => { e.preventDefault(); if (!at) { setNoDate(true); document.getElementById("m-at")?.focus(); return; } save.mutate({ at: new Date(at).toISOString(), location, hostId: hostId || null }); }}>
      <div className="row spread"><b>{m ? txt.editTheMeeting : txt.nextMeeting}</b>{m && <button type="button" className="linkish small muted" onClick={onDone}><CloseIcon width={16} height={16} /></button>}</div>
      <div className="field"><label htmlFor="m-at">{txt.whenYourLocalTime}</label><input id="m-at" className={`input${noDate ? " err" : ""}`} type="datetime-local" value={at} aria-invalid={noDate} onChange={(e) => { setAt(e.target.value); setNoDate(false); }} />
        {noDate && <span className="small warn" role="alert">{txt.pickADateAnd}</span>}</div>
      <div className="field"><label htmlFor="m-loc">{txt.whereOrACall}</label><input id="m-loc" className="input" value={location} maxLength={120} placeholder={txt.aCaféOrA} onChange={(e) => setLocation(e.target.value)} /></div>
      <div className="field"><label htmlFor="m-host">{txt.hostOptional}</label>
        <select id="m-host" className="input" value={hostId} onChange={(e) => setHostId(e.target.value)}>
          <option value="">{txt.nobodyInParticular}</option>
          {members.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select></div>
      {moving && <p className="muted small">{txt.movingTheDateAsks}</p>}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn primary" type="submit" disabled={save.isPending}>{txt.save}</button>
        {m && <button className="btn ghost" type="button" disabled={save.isPending} onClick={() => save.mutate({ at: null })}>{txt.clearTheDate}</button>}
        {save.isError && <p className="error">{describeError(save.error)}</p>}
      </div>
    </form>
  );
}

